import { Player } from "@lottiefiles/react-lottie-player";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";

interface LottieShowcaseProps {
  src: string;
  minHeight?: number;
  flipX?: boolean;
  overlay?: string;
}

export default function LottieShowcase({
  src,
  minHeight = 340,
  flipX = false,
  overlay,
}: LottieShowcaseProps) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);

  const handleEvent = (event: string) => {
    if (event === "ready" || event === "load") {
      setIsReady(true);
    }
    if (event === "error") {
      setHasError(true);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        position: "relative",
        borderRadius: 4,
        overflow: "hidden",
        minHeight,
        borderColor: isDarkMode
          ? "rgba(91, 145, 191, 0.42)"
          : "rgba(94, 163, 221, 0.30)",
        background: isDarkMode
          ? "linear-gradient(145deg, rgba(11,26,41,0.92), rgba(12,32,52,0.78))"
          : "linear-gradient(145deg, rgba(214,236,252,0.92), rgba(235,247,255,0.72))",
        boxShadow: isDarkMode
          ? "0 18px 40px rgba(3, 11, 19, 0.36)"
          : "0 18px 40px rgba(27, 58, 96, 0.14)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: isDarkMode
            ? "radial-gradient(circle at 18% 24%, rgba(62, 142, 205, 0.25), rgba(15,32,50,0) 58%), radial-gradient(circle at 84% 84%, rgba(79, 150, 211, 0.2), rgba(15,32,50,0) 48%)"
            : "radial-gradient(circle at 18% 24%, rgba(120, 199, 255, 0.24), rgba(230,244,255,0) 55%), radial-gradient(circle at 84% 84%, rgba(131, 210, 255, 0.18), rgba(230,244,255,0) 46%)",
        }}
      />

      {!isReady && !hasError ? (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{ position: "absolute", inset: 0, borderRadius: 0 }}
        />
      ) : null}

      {hasError ? (
        <Stack
          sx={{ minHeight }}
          alignItems="center"
          justifyContent="center"
          px={3}
          spacing={1}
        >
          <Typography color="text.secondary" align="center">
            Nao foi possivel carregar esta animacao.
          </Typography>
        </Stack>
      ) : (
        <Box sx={{ transform: flipX ? "scaleX(-1)" : "none" }}>
          <Player
            autoplay
            loop
            renderer="svg"
            src={src}
            onEvent={handleEvent}
            style={{
              width: "100%",
              height: "100%",
              minHeight: `${minHeight}px`,
              filter: isDarkMode
                ? "saturate(0.82) brightness(0.88) contrast(1.06)"
                : "saturate(0.88) contrast(1.02)",
            }}
          />
        </Box>
      )}

      {overlay ? (
        <Box
          sx={{
            position: "absolute",
            insetX: 0,
            bottom: 0,
            p: 2.5,
            background: isDarkMode
              ? "linear-gradient(180deg, rgba(3,9,15,0) 0%, rgba(3,9,15,0.88) 100%)"
              : "linear-gradient(180deg, rgba(2,16,35,0) 0%, rgba(2,16,35,0.88) 100%)",
          }}
        >
          <Typography
            sx={{ color: isDarkMode ? "#D7EAFB" : "#E6F4FF", fontWeight: 700 }}
          >
            {overlay}
          </Typography>
        </Box>
      ) : null}
    </Card>
  );
}
